export type EditorialStoryLocale = 'fr' | 'en' | 'es' | 'pt' | 'ja' | 'ko' | 'de' | 'it' | 'zh'

type EditorialSection = {
  eyebrow: string
  intro: string
  rest: string
}

type EditorialCraftSection = {
  eyebrow: string
  body: string
}

type EditorialRecordGroup = {
  eyebrow: string
  text: string
}

type EditorialRecordQuote = {
  text: string
  footer?: string
  footerHref?: string
}

export type EditorialStoryCopy = {
  origin: EditorialSection
  house: EditorialSection
  craft: EditorialCraftSection
}

export type EditorialRecordCopy = {
  eyebrow: string
  title: string
  groups: readonly EditorialRecordGroup[]
  quotes: readonly EditorialRecordQuote[]
}

export type EditorialUiCopy = {
  readMore: string
  readLess: string
}

const STORY_COPY: Record<EditorialStoryLocale, EditorialStoryCopy> = {
  fr: {
    origin: {
      eyebrow: 'L\'origine',
      intro: 'Il y a des destins qui s\u2019écrivent à l\u2019encre du cuir. Tout part de Normandie\u00A0— la capitale européenne du cuir au Moyen Âge, une terre où cette matière vivante imprégnait les pierres, les rivières, les mains des hommes. C\u2019est là que naît le fondateur de la maison. Et sans le savoir encore, c\u2019est là que la marque commence.',
      rest: `<p>Son premier geste de photographe\u00A0? Pointer son objectif sur les tanneries. Pas les châteaux, pas les falaises. Les tanneries. Comme si quelque chose, déjà, le ramenait toujours au même endroit.</p>
<p>De part et d\u2019autre de son arbre familial, un grand-père bottier, l\u2019autre gérant d\u2019un magasin de chaussures de luxe. L\u2019héritage ne se dit pas\u00A0— il se ressent dans les mains.</p>
<p>Il devient ingénieur. Puis la lumière l\u2019appelle autrement\u00A0: il devient photographe. Ses images paraissent dans la presse. Il observe, il cadre, il retient. Pendant des années, il photographie des femmes sur les podiums\u00A0— cette façon particulière qu\u2019elles ont de marcher, de s\u2019appartenir, d\u2019occuper l\u2019espace avec une assurance tranquille.</p>
<p>Puis vient le digital. Il conçoit, il structure, il construit — des produits, des interfaces, des systèmes qui doivent tenir debout sans jamais se voir. Structure. Image. Construction. Trois regards qui ne le quittent plus.</p>
<p>Puis vient le Mexique. Pays mystique, pays de rupture. Quelque chose se déplace en lui, silencieusement mais définitivement. La trajectoire change. On ne sait pas toujours pourquoi certains endroits nous transforment\u00A0— on le sait seulement après.</p>
<p>De retour, il ressent ce besoin rare et précis\u00A0: matérialiser. Non plus capturer le monde en image, mais le toucher, le construire, le tenir entre ses mains.</p>
<p>Cuir. Aiguille. Fil.</p>
<p>Et dans ce geste ancestral, dans cette lenteur assumée, il trouve enfin ce qu\u2019il cherchait sans le formuler\u00A0: la forme juste. Trouvée à la main.</p>`
    },
    house: {
      eyebrow: 'Elles',
      intro: 'Elles n\u2019ont pas besoin qu\u2019on les présente. Elles entrent dans une pièce et quelque chose se dépose\u00A0— une certitude tranquille, une façon d\u2019occuper l\u2019espace sans le revendiquer. Ce sont des femmes qui décident, qui plaident, qui créent, qui dirigent. Des femmes dont la présence précède les mots.',
      rest: `<p>Ce sont elles qui ont trouvé Kendrick.</p>
<p>Ou peut-être l\u2019inverse.</p>
<p>Leurs chaussures sont apparues dans les pages de Madame Figaro, de L\u2019Officiel, de Vanity Fair. Sur les plateaux de la télévision française. Pas parce que la maison cherchait les projecteurs\u00A0— mais parce que ce genre de femmes attire naturellement la lumière. Et qu\u2019elles savent reconnaître ce qui est fait pour durer.</p>
<p>Kendrick ne fait pas de bruit. Il fabrique des chaussures à la main, en cuir, avec la précision de quelqu\u2019un qui a passé des années à observer ce que la posture dit de l\u2019intérieur. La forme juste. Celle qui ne cherche pas à convaincre\u00A0— elle s\u2019impose d\u2019elle-même.</p>
<p>Comme elles.</p>`
    },
    craft: {
      eyebrow: 'Le produit',
      body: 'Conçu à Paris. Façonné en Italie, dans la région des Marches. Talon de 110 mm pensé comme une architecture, jamais comme un accessoire. Cuir mat ou verni\u00A0: chaque finition est un parti pris. Le confort est inscrit dans la construction, non rapporté ensuite.'
    }
  },
  en: {
    origin: {
      eyebrow: 'The origin',
      intro: 'Some destinies are written in leather. It begins in Normandy\u00A0— the leather capital of Europe in the Middle Ages, a land where this living material seeped into the stones, the rivers, the hands of those who shaped it. This is where the story begins. And without knowing it yet, this is where the house begins too.',
      rest: `<p>His first instinct as a photographer? To point his lens at the tanneries. Not the castles, not the cliffs. The tanneries. As if something, already, kept drawing him back to the same place.</p>
<p>On both sides of his family tree, one grandfather was a bottier and the other managed a luxury footwear store. Heritage is not spoken\u00A0— it is felt in the hands.</p>
<p>He becomes an engineer. Then light calls him differently: he becomes a photographer. His images appear in the press. He observes, he frames, he retains. For years, he photographs women on the runways\u00A0— that particular way they have of walking, of belonging to themselves, of occupying space with quiet confidence.</p>
<p>Then comes digital. He designs, he structures, he builds — products, interfaces, systems that must stand without ever being seen. Structure. Image. Construction. Three ways of seeing that never leave him.</p>
<p>Then comes Mexico. A mystical country, a country of rupture. Something shifts within him, silently but definitively. The trajectory changes. We do not always know why certain places transform us\u00A0— we only know it after.</p>
<p>Back home, he feels that rare and precise need: to materialise. No longer to capture the world in images, but to touch it, to build it, to hold it in his hands.</p>
<p>Leather. Needle. Thread.</p>
<p>And in that ancestral gesture, in that deliberate slowness, he finally finds what he had been searching for without knowing how to name it: the right form. Found by hand.</p>`
    },
    house: {
      eyebrow: 'The house',
      intro: 'They don\u2019t need an introduction. They walk into a room and something settles\u00A0— a quiet certainty, a way of occupying space without claiming it. These are women who decide, who argue, who create, who lead. Women whose presence precedes their words.',
      rest: `<p>They found Kendrick.</p>
<p>Or perhaps it was the other way around.</p>
<p>Their shoes have appeared in the pages of Madame Figaro, L\u2019Officiel, Vanity Fair. On French television sets. Not because the house sought the spotlight\u00A0— but because that kind of woman naturally draws the light. And they know how to recognise what is made to last.</p>
<p>Kendrick makes no noise. He crafts shoes by hand, in leather, with the precision of someone who spent years observing what posture reveals from within. The right form. The one that doesn\u2019t try to convince\u00A0— it simply asserts itself.</p>
<p>Like them.</p>`
    },
    craft: {
      eyebrow: 'The craft',
      body: 'Designed in Paris. Crafted in Italy, in the Marche region. A structural 110 mm heel, engineered as architecture\u00A0— never as an accessory. Matte or patent leather, each finish a deliberate choice. Comfort built into the construction, never bolted on.'
    }
  },
  es: {
    origin: {
      eyebrow: 'El origen',
      intro: 'Hay destinos que se escriben en cuero. Todo empieza en Normandía\u00A0— capital europea del cuero en la Edad Media, una tierra donde esta materia viva impregnaba las piedras, los ríos y las manos de quienes la trabajaban. Ahí empieza la historia. Y, sin saberlo todavía, ahí empieza también la maison.',
      rest: `<p>Su primer gesto como fotógrafo fue dirigir el objetivo hacia las tenerías. No los castillos, no los acantilados. Las tenerías. Como si algo, ya entonces, lo llevara siempre al mismo lugar.</p>
<p>A ambos lados de su árbol familiar, un abuelo era botero y el otro dirigía una tienda de calzado de lujo. La herencia no se explica\u00A0— se siente en las manos.</p>
<p>Se convierte en ingeniero. Después la luz lo llama de otra manera: se convierte en fotógrafo. Sus imágenes aparecen en la prensa. Observa, encuadra, retiene. Durante años fotografía a mujeres en pasarelas\u00A0— esa forma particular de caminar, de pertenecerse, de ocupar el espacio con una seguridad tranquila.</p>
<p>Después llega lo digital. Diseña, estructura, construye — productos, interfaces, sistemas que deben sostenerse sin dejarse ver nunca. Estructura. Imagen. Construcción. Tres miradas que ya no lo abandonan.</p>
<p>Después llega México. País místico, país de ruptura. Algo se desplaza en él, silenciosa y definitivamente. La trayectoria cambia. No siempre sabemos por qué ciertos lugares nos transforman\u00A0— solo lo sabemos después.</p>
<p>Al volver, siente una necesidad rara y precisa: materializar. Ya no capturar el mundo en imágenes, sino tocarlo, construirlo, sostenerlo entre las manos.</p>
<p>Cuero. Aguja. Hilo.</p>
<p>Y en ese gesto ancestral, en esa lentitud asumida, encuentra por fin lo que buscaba sin saber nombrarlo: la forma justa. Encontrada a mano.</p>`
    },
    house: {
      eyebrow: 'La maison',
      intro: 'No necesitan presentación. Entran en una habitación y algo se instala\u00A0— una certeza tranquila, una manera de ocupar el espacio sin reclamarlo. Son mujeres que deciden, defienden, crean y dirigen. Mujeres cuya presencia precede a sus palabras.',
      rest: `<p>Ellas encontraron Kendrick.</p>
<p>O quizá fue al revés.</p>
<p>Sus zapatos han aparecido en las páginas de Madame Figaro, L'Officiel y Vanity Fair. En platós de la televisión francesa. No porque la maison buscara los focos\u00A0— sino porque ese tipo de mujer atrae la luz de forma natural. Y sabe reconocer lo que está hecho para durar.</p>
<p>Kendrick no hace ruido. Fabrica zapatos a mano, en cuero, con la precisión de quien pasó años observando lo que la postura revela desde dentro. La forma justa. La que no intenta convencer\u00A0— se impone por sí misma.</p>
<p>Como ellas.</p>`
    },
    craft: {
      eyebrow: 'El producto',
      body: 'Diseñado en París. Elaborado en Italia, en la región de Las Marcas. Un tacón de 110 mm pensado como arquitectura, nunca como accesorio. Cuero mate o charol: cada acabado es una elección deliberada. El confort está integrado en la construcción, no añadido después.'
    }
  },
  pt: {
    origin: {
      eyebrow: 'A origem',
      intro: 'Há destinos que são escritos em couro. Tudo começa na Normandia\u00A0— capital europeia do couro na Idade Média, uma terra onde esta matéria viva penetrava nas pedras, nos rios e nas mãos de quem a trabalhava. É aqui que a história começa. E, sem o saber ainda, é aqui que a maison também começa.',
      rest: `<p>O seu primeiro gesto como fotógrafo? Apontar a objetiva para os curtumes. Não para os castelos, não para as falésias. Para os curtumes. Como se algo, desde o início, o levasse sempre ao mesmo lugar.</p>
<p>Dos dois lados da árvore familiar, um avô era sapateiro e o outro geria uma loja de calçado de luxo. A herança não se diz\u00A0— sente-se nas mãos.</p>
<p>Torna-se engenheiro. Depois a luz chama-o de outra forma: torna-se fotógrafo. As suas imagens aparecem na imprensa. Observa, enquadra, retém. Durante anos fotografa mulheres nas passerelles\u00A0— aquela forma particular de caminhar, de pertencer a si próprias, de ocupar o espaço com uma confiança tranquila.</p>
<p>Depois vem o digital. Concebe, estrutura, constrói — produtos, interfaces, sistemas que devem sustentar-se sem nunca serem vistos. Estrutura. Imagem. Construção. Três olhares que já não o abandonam.</p>
<p>Depois vem o México. País místico, país de ruptura. Algo se desloca nele, silenciosa mas definitivamente. A trajetória muda. Nem sempre sabemos porque certos lugares nos transformam\u00A0— só o sabemos depois.</p>
<p>De regresso, sente essa necessidade rara e precisa: materializar. Já não capturar o mundo em imagem, mas tocá-lo, construí-lo, segurá-lo nas mãos.</p>
<p>Couro. Agulha. Linha.</p>
<p>E nesse gesto ancestral, nessa lentidão assumida, encontra finalmente o que procurava sem saber nomear: a forma certa. Encontrada à mão.</p>`
    },
    house: {
      eyebrow: 'A maison',
      intro: 'Elas não precisam de apresentação. Entram numa sala e algo se instala\u00A0— uma certeza tranquila, uma forma de ocupar o espaço sem o reivindicar. São mulheres que decidem, argumentam, criam e lideram. Mulheres cuja presença antecede as palavras.',
      rest: `<p>Foram elas que encontraram Kendrick.</p>
<p>Ou talvez tenha sido o contrário.</p>
<p>Os seus sapatos apareceram nas páginas da Madame Figaro, da L'Officiel e da Vanity Fair. Em estúdios da televisão francesa. Não porque a maison procurasse os holofotes\u00A0— mas porque este tipo de mulher atrai naturalmente a luz. E sabe reconhecer o que é feito para durar.</p>
<p>Kendrick não faz ruído. Cria sapatos à mão, em couro, com a precisão de quem passou anos a observar o que a postura revela por dentro. A forma certa. Aquela que não tenta convencer\u00A0— impõe-se por si.</p>
<p>Como elas.</p>`
    },
    craft: {
      eyebrow: 'O produto',
      body: 'Desenhado em Paris. Fabricado em Itália, na região das Marcas. Um salto de 110 mm pensado como arquitetura, nunca como acessório. Couro mate ou verniz: cada acabamento é uma escolha deliberada. O conforto está inscrito na construção, não acrescentado depois.'
    }
  },
  ja: {
    origin: {
      eyebrow: '起源',
      intro: '革で綴られる運命がある。物語はノルマンディーから始まります\u00A0— 中世ヨーロッパにおける革の都。石に、川に、そして革を扱う人々の手に、この生きた素材が染み込んでいた土地です。ここから物語は始まりました。そしてまだ誰も知らないまま、メゾンもここから始まっていたのです。',
      rest: `<p>写真家としての最初の衝動は、レンズをタンナーの工房へ向けることでした。城でも、断崖でもなく、革をなめす場所へ。まるで何かが、すでに彼を同じ場所へ引き戻していたかのように。</p>
<p>家系の両側には、靴職人だった祖父と、ラグジュアリーシューズ店を営んでいた祖父がいました。受け継がれるものは、言葉ではなく手の感覚に宿ります。</p>
<p>彼はエンジニアになります。その後、光が別のかたちで彼を呼び、写真家になります。写真はメディアに掲載され、彼は観察し、構図を決め、記憶する。何年もの間、ランウェイを歩く女性たちを撮り続けました。自分自身に属し、静かな自信で空間を満たす、あの独特の歩き方を。</p>
<p>その後、デジタルの時代がやってきます。プロダクトを設計し、構造をつくり、決して姿を見せることのないシステムを組み立てる — 構造、イメージ、構築。この三つの視点は、それ以来ずっと彼から離れません。</p>
<p>そしてメキシコへ。神秘的で、断絶をもたらす国。何かが彼の中で、静かに、しかし決定的に動きました。道筋が変わったのです。ある場所がなぜ私たちを変えるのか、その時はわかりません。あとになって初めてわかるのです。</p>
<p>戻ってきた彼の中に、稀で明確な欲求が生まれます。形にすること。世界を写真に収めるのではなく、触れ、組み立て、手の中に持つこと。</p>
<p>革。針。糸。</p>
<p>その祖先的な手仕事と、意志ある遅さの中で、彼はようやく探していたものを見つけます。まだ名前を持たなかったもの。正しい形。手で見つけられた形です。</p>`
    },
    house: {
      eyebrow: 'メゾン',
      intro: '彼女たちに紹介は必要ありません。部屋に入るだけで、静かな確信がそこに宿ります。主張せずに空間を占める在り方。決断し、語り、創造し、導く女性たち。言葉より先に存在感が届く女性たちです。',
      rest: `<p>彼女たちがKendrickを見つけました。</p>
<p>あるいは、その逆だったのかもしれません。</p>
<p>その靴はMadame Figaro、L'Officiel、Vanity Fairの誌面に登場し、フランスのテレビスタジオでも履かれてきました。メゾンがスポットライトを求めたからではありません。そのような女性は自然に光を引き寄せ、長く残るものを見分けることを知っているからです。</p>
<p>Kendrickは声高ではありません。革を用い、手で靴を作ります。姿勢が内側から何を語るのかを長年見つめてきた人の精度で。正しい形。説得しようとしない形。ただ、そこに在るだけで成立する形。</p>
<p>彼女たちのように。</p>`
    },
    craft: {
      eyebrow: '製品',
      body: 'パリでデザインされ、イタリアのマルケ州で形にされます。110mmのヒールは、アクセサリーではなく建築として設計された構造。マットレザーもパテントレザーも、それぞれが意志ある選択です。快適さは後から足されるものではなく、構造そのものに組み込まれています。'
    }
  },
  ko: {
    origin: {
      eyebrow: '기원',
      intro: '가죽으로 쓰이는 운명이 있습니다. 이야기는 노르망디에서 시작됩니다\u00A0— 중세 유럽의 가죽 수도였던 곳, 이 살아 있는 소재가 돌과 강, 그리고 그것을 다루던 사람들의 손에 스며들었던 땅입니다. 이곳에서 이야기가 시작됩니다. 그리고 아직 알지 못한 채, 메종 역시 이곳에서 시작되고 있었습니다.',
      rest: `<p>사진가로서 그의 첫 본능은 렌즈를 무두질 공방으로 향하게 하는 것이었습니다. 성도, 절벽도 아닌 무두질 공방. 이미 무언가가 그를 같은 장소로 계속 이끌고 있었던 것처럼.</p>
<p>가족의 양쪽에는 부츠 장인이었던 할아버지와 럭셔리 슈즈 매장을 운영했던 할아버지가 있었습니다. 유산은 말로 설명되는 것이 아니라 손에서 느껴지는 것입니다.</p>
<p>그는 엔지니어가 됩니다. 이후 빛이 다른 방식으로 그를 부르고, 그는 사진가가 됩니다. 그의 이미지는 언론에 실립니다. 그는 관찰하고, 프레임을 잡고, 기억합니다. 수년 동안 런웨이 위의 여성들을 촬영하며 그들이 걷고, 자신에게 속하고, 조용한 확신으로 공간을 차지하는 특별한 방식을 바라봅니다.</p>
<p>그다음 디지털이 찾아옵니다. 그는 제품을 설계하고, 구조를 세우고, 결코 눈에 보이지 않아야 하는 시스템을 만듭니다. 구조. 이미지. 구축. 그를 떠나지 않는 세 가지 시선입니다.</p>
<p>그리고 멕시코가 찾아옵니다. 신비롭고 단절을 만드는 나라. 그의 안에서 무언가가 조용하지만 결정적으로 움직입니다. 길이 바뀝니다. 어떤 장소가 왜 우리를 변화시키는지는 그 순간 알 수 없습니다. 지나고 나서야 알게 됩니다.</p>
<p>돌아온 그는 드물고도 정확한 필요를 느낍니다. 물질화하는 것. 더 이상 세계를 이미지로 붙잡는 것이 아니라, 만지고, 만들고, 손에 쥐는 것.</p>
<p>가죽. 바늘. 실.</p>
<p>그 오래된 손의 동작과 의도된 느림 속에서, 그는 마침내 이름 붙이지 못한 채 찾고 있던 것을 발견합니다. 올바른 형태. 손으로 찾아낸 형태입니다.</p>`
    },
    house: {
      eyebrow: '메종',
      intro: '그녀들에게 소개는 필요 없습니다. 방에 들어서는 순간 무언가가 자리 잡습니다\u00A0— 조용한 확신, 주장하지 않고 공간을 차지하는 방식. 결정하고, 말하고, 창조하고, 이끄는 여성들. 말보다 먼저 존재감이 도착하는 여성들입니다.',
      rest: `<p>그녀들이 Kendrick을 발견했습니다.</p>
<p>혹은 그 반대였을지도 모릅니다.</p>
<p>그 신발들은 Madame Figaro, L'Officiel, Vanity Fair 지면에 등장했고, 프랑스 TV 스튜디오에서도 착용되었습니다. 메종이 조명을 찾았기 때문이 아닙니다\u00A0— 그런 여성은 자연스럽게 빛을 끌어당기고, 오래 지속될 것을 알아보기 때문입니다.</p>
<p>Kendrick은 큰 소리를 내지 않습니다. 가죽으로, 손으로 신발을 만듭니다. 자세가 내면에서 무엇을 드러내는지 오랫동안 관찰해온 사람의 정밀함으로. 올바른 형태. 설득하려 하지 않는 형태\u00A0— 스스로 존재를 드러내는 형태.</p>
<p>그녀들처럼.</p>`
    },
    craft: {
      eyebrow: '제품',
      body: '파리에서 디자인하고 이탈리아 마르케 지역에서 제작합니다. 110mm 힐은 액세서리가 아니라 건축처럼 설계된 구조입니다. 매트 가죽과 페이턴트 가죽, 각 마감은 의도된 선택입니다. 편안함은 나중에 덧붙이는 것이 아니라 구조 안에 들어 있습니다.'
    }
  },
  de: {
    origin: {
      eyebrow: 'Der Ursprung',
      intro: 'Es gibt Schicksale, die in Leder geschrieben sind. Alles beginnt in der Normandie\u00A0— im Mittelalter die Lederhauptstadt Europas, ein Land, in dem dieses lebendige Material in Steine, Flüsse und die Hände der Menschen einsickerte, die es formten. Hier beginnt die Geschichte. Und ohne es schon zu wissen, beginnt hier auch das Haus.',
      rest: `<p>Sein erster Impuls als Fotograf? Die Kamera auf die Gerbereien zu richten. Nicht auf die Schlösser, nicht auf die Klippen. Auf die Gerbereien. Als würde ihn etwas schon damals immer an denselben Ort zurückführen.</p>
<p>Auf beiden Seiten seines Stammbaums: ein Großvater war Bottier, der andere führte ein Geschäft für Luxusschuhe. Erbe wird nicht ausgesprochen\u00A0— es wird in den Händen gespürt.</p>
<p>Er wird Ingenieur. Dann ruft ihn das Licht anders: Er wird Fotograf. Seine Bilder erscheinen in der Presse. Er beobachtet, rahmt, behält. Jahrelang fotografiert er Frauen auf Laufstegen\u00A0— diese besondere Art zu gehen, sich selbst zu gehören, mit stiller Sicherheit Raum einzunehmen.</p>
<p>Dann kommt das Digitale. Er entwirft, er strukturiert, er baut — Produkte, Schnittstellen, Systeme, die tragen müssen, ohne je gesehen zu werden. Struktur. Bild. Konstruktion. Drei Blickweisen, die ihn seither nicht mehr verlassen.</p>
<p>Dann kommt Mexiko. Ein mystisches Land, ein Land des Bruchs. Etwas verschiebt sich in ihm, leise, aber endgültig. Die Richtung ändert sich. Wir wissen nicht immer, warum bestimmte Orte uns verwandeln\u00A0— wir wissen es erst danach.</p>
<p>Zurück spürt er ein seltenes und präzises Bedürfnis: materialisieren. Die Welt nicht mehr nur im Bild festhalten, sondern sie berühren, bauen, in den Händen halten.</p>
<p>Leder. Nadel. Faden.</p>
<p>Und in dieser uralten Geste, in dieser bewusst angenommenen Langsamkeit, findet er endlich, wonach er gesucht hatte, ohne es benennen zu können: die richtige Form. Von Hand gefunden.</p>`
    },
    house: {
      eyebrow: 'Das Haus',
      intro: 'Sie brauchen keine Vorstellung. Sie betreten einen Raum und etwas setzt sich\u00A0— eine stille Gewissheit, eine Art, Raum einzunehmen, ohne ihn einzufordern. Frauen, die entscheiden, argumentieren, schaffen und führen. Frauen, deren Präsenz ihren Worten vorausgeht.',
      rest: `<p>Sie haben Kendrick gefunden.</p>
<p>Oder vielleicht war es umgekehrt.</p>
<p>Ihre Schuhe erschienen in Madame Figaro, L'Officiel und Vanity Fair. Auf französischen Fernsehsets. Nicht, weil das Haus das Rampenlicht suchte\u00A0— sondern weil diese Art von Frau Licht auf natürliche Weise anzieht. Und weil sie erkennt, was für Dauer gemacht ist.</p>
<p>Kendrick macht keinen Lärm. Das Haus fertigt Schuhe von Hand, aus Leder, mit der Präzision von jemandem, der jahrelang beobachtet hat, was Haltung von innen erzählt. Die richtige Form. Eine Form, die nicht überzeugen will\u00A0— sie behauptet sich von selbst.</p>
<p>Wie sie.</p>`
    },
    craft: {
      eyebrow: 'Das Produkt',
      body: 'Entworfen in Paris. Gefertigt in Italien, in der Region Marken. Ein 110-mm-Absatz, als Architektur gedacht, niemals als Accessoire. Mattes Leder oder Lackleder: jedes Finish ist eine bewusste Entscheidung. Komfort ist in die Konstruktion eingeschrieben, nicht nachträglich hinzugefügt.'
    }
  },
  it: {
    origin: {
      eyebrow: "L'origine",
      intro: 'Ci sono destini scritti nella pelle. Tutto inizia in Normandia\u00A0— capitale europea della pelle nel Medioevo, una terra in cui questa materia viva penetrava nelle pietre, nei fiumi, nelle mani di chi la lavorava. È qui che comincia la storia. E, senza saperlo ancora, è qui che comincia anche la maison.',
      rest: `<p>Il suo primo gesto da fotografo? Puntare l'obiettivo sulle concerie. Non sui castelli, non sulle scogliere. Sulle concerie. Come se qualcosa, già allora, lo riportasse sempre nello stesso luogo.</p>
<p>Da entrambi i lati dell'albero familiare, un nonno era stivalaio e l'altro gestiva un negozio di calzature di lusso. L'eredità non si racconta\u00A0— si sente nelle mani.</p>
<p>Diventa ingegnere. Poi la luce lo chiama in un altro modo: diventa fotografo. Le sue immagini appaiono nella stampa. Osserva, inquadra, trattiene. Per anni fotografa donne sulle passerelle\u00A0— quel modo particolare di camminare, di appartenere a se stesse, di occupare lo spazio con una sicurezza quieta.</p>
<p>Poi arriva il digitale. Progetta, struttura, costruisce — prodotti, interfacce, sistemi che devono reggere senza mai essere visti. Struttura. Immagine. Costruzione. Tre sguardi che non lo lasciano più.</p>
<p>Poi arriva il Messico. Paese mistico, paese di rottura. Qualcosa si sposta dentro di lui, silenziosamente ma definitivamente. La traiettoria cambia. Non sappiamo sempre perché certi luoghi ci trasformino\u00A0— lo capiamo solo dopo.</p>
<p>Al ritorno sente un bisogno raro e preciso: materializzare. Non più catturare il mondo in immagine, ma toccarlo, costruirlo, tenerlo tra le mani.</p>
<p>Pelle. Ago. Filo.</p>
<p>E in quel gesto ancestrale, in quella lentezza scelta, trova finalmente ciò che cercava senza saperlo nominare: la forma giusta. Trovata a mano.</p>`
    },
    house: {
      eyebrow: 'La maison',
      intro: 'Non hanno bisogno di presentazioni. Entrano in una stanza e qualcosa si posa\u00A0— una certezza quieta, un modo di occupare lo spazio senza rivendicarlo. Sono donne che decidono, argomentano, creano, guidano. Donne la cui presenza precede le parole.',
      rest: `<p>Sono loro ad aver trovato Kendrick.</p>
<p>O forse è stato il contrario.</p>
<p>Le loro scarpe sono apparse sulle pagine di Madame Figaro, L'Officiel, Vanity Fair. Nei set della televisione francese. Non perché la maison cercasse i riflettori\u00A0— ma perché questo tipo di donna attira naturalmente la luce. E sa riconoscere ciò che è fatto per durare.</p>
<p>Kendrick non fa rumore. Realizza scarpe a mano, in pelle, con la precisione di chi ha passato anni a osservare ciò che la postura racconta dall'interno. La forma giusta. Quella che non cerca di convincere\u00A0— si impone da sé.</p>
<p>Come loro.</p>`
    },
    craft: {
      eyebrow: 'Il prodotto',
      body: 'Disegnato a Parigi. Realizzato in Italia, nella regione delle Marche. Un tacco da 110 mm pensato come architettura, mai come accessorio. Pelle opaca o verniciata: ogni finitura è una scelta precisa. Il comfort è inscritto nella costruzione, non aggiunto dopo.'
    }
  },
  zh: {
    origin: {
      eyebrow: '起源',
      intro: '有些命运写在皮革里。故事始于诺曼底\u00A0— 中世纪欧洲的皮革之都。在这片土地上，这种有生命的材质渗入石头、河流，也渗入塑造它的人们手中。故事从这里开始。而在尚未被意识到的时候，这个品牌也从这里开始。',
      rest: `<p>作为摄影师的第一个直觉，是把镜头对准制革厂。不是城堡，不是悬崖，而是制革厂。仿佛某种力量从一开始就把他带回同一个地方。</p>
<p>在他的家族两侧，一位祖父是靴匠，另一位祖父经营高级鞋履店。传承并不总是被说出\u00A0— 它会在手中被感知。</p>
<p>他成为工程师。后来，光以另一种方式召唤他，他成为摄影师。他的影像出现在媒体上。他观察、构图、记住。多年来，他拍摄T台上的女性\u00A0— 她们行走、属于自己、以安静自信占据空间的特殊方式。</p>
<p>接着，数字世界到来。他设计产品，构建结构，搭建那些必须稳固却从不显形的系统。结构。影像。构造。这三种视角从此再未离开过他。</p>
<p>然后是墨西哥。神秘之地，也是转折之地。某些东西在他体内安静而彻底地移动。轨迹改变了。我们并不总在当下明白某个地方为何改变我们\u00A0— 往往是在后来才知道。</p>
<p>归来之后，他感到一种少见而准确的需要：让事物成为实体。不再只是用影像捕捉世界，而是触摸它、建造它、把它握在手中。</p>
<p>皮革。针。线。</p>
<p>在这种古老的动作和有意识的缓慢之中，他终于找到了那个一直在寻找却无法命名的东西：正确的形。由手找到的形。</p>`
    },
    house: {
      eyebrow: '品牌',
      intro: '她们不需要被介绍。她们走进一个空间，某种东西便自然落定\u00A0— 一种安静的确定感，一种无需宣示却能占据空间的方式。她们决策、表达、创造、引领。她们的存在感先于语言抵达。',
      rest: `<p>是她们找到了Kendrick。</p>
<p>也许反过来也成立。</p>
<p>这些鞋履出现在Madame Figaro、L'Officiel、Vanity Fair的页面中，也出现在法国电视节目现场。并不是因为品牌追逐聚光灯\u00A0— 而是这种女性自然吸引光线。她们也知道如何辨认真正能够留下来的东西。</p>
<p>Kendrick不喧哗。它以皮革手工制作鞋履，带着一种精确：来自多年观察姿态如何从内在显现。正确的形。不试图说服，却自然成立。</p>
<p>如她们一样。</p>`
    },
    craft: {
      eyebrow: '产品',
      body: '设计于巴黎。制作于意大利马尔凯地区。110毫米鞋跟被作为结构来设计，而非装饰。哑光皮革或漆皮，每一种表面处理都是有意识的选择。舒适被写入结构本身，而不是事后添加。'
    }
  }
}

const RECORD_COPY: Record<EditorialStoryLocale, EditorialRecordCopy> = {
  fr: {
    eyebrow: 'Dans les faits',
    title: "Vu à l'écran. Lu dans la presse.",
    groups: [
      {
        eyebrow: 'Télévision',
        text: "France TV · TF1 · Canal+ · beIN Sports · M6 · T18. Portées à l'écran par Mélanie Taravant, Maya Lauque, Marie Drucker, Karine Ferri, Anggun, Caroline Roux, Isabelle Ithurburu, Laurie Cholewa, Hélène Mannarino, Pauline Sanzey, Ava Djamshidi."
      },
      {
        eyebrow: 'Presse',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '"Ces bottines Kendrick Paris cochent toutes les cases."',
        footer: '- Kahina Sekkai, rédactrice en chef web, Vanity Fair France'
      },
      {
        text: '"Ce sont les plus confortables de la terre, en plus d’être vraiment belles, j’ai que des compliments à chaque fois que je les porte."',
        footer: '- Ava Djamshidi, T18 (TV)',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  en: {
    eyebrow: 'On record',
    title: 'Seen on screen. Read in print.',
    groups: [
      {
        eyebrow: 'Television',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18. Worn on air by Mélanie Taravant, Maya Lauque, Marie Drucker, Karine Ferri, Anggun, Caroline Roux, Isabelle Ithurburu, Laurie Cholewa, Hélène Mannarino, Pauline Sanzey, Ava Djamshidi.'
      },
      {
        eyebrow: 'Press',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '"These Kendrick Paris ankle boots tick every box."',
        footer: '- Kahina Sekkai, Editor-in-Chief Web, Vanity Fair France'
      },
      {
        text: '"They are the most comfortable on earth, and on top of being truly beautiful, I get nothing but compliments every time I wear them."',
        footer: '- Ava Djamshidi, T18 (TV)',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  es: {
    eyebrow: 'En los hechos',
    title: 'Visto en pantalla. Leído en prensa.',
    groups: [
      {
        eyebrow: 'Televisión',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18. Llevadas en pantalla por Mélanie Taravant, Maya Lauque, Marie Drucker, Karine Ferri, Anggun, Caroline Roux, Isabelle Ithurburu, Laurie Cholewa, Hélène Mannarino, Pauline Sanzey y Ava Djamshidi.'
      },
      {
        eyebrow: 'Prensa',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '"Estos botines Kendrick Paris lo tienen todo."',
        footer: '- Kahina Sekkai, redactora jefe web, Vanity Fair France'
      },
      {
        text: '"Son los más cómodos del mundo y, además de ser realmente bonitos, recibo cumplidos cada vez que los llevo."',
        footer: '- Ava Djamshidi, T18 (TV)',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  pt: {
    eyebrow: 'Nos factos',
    title: 'Visto no ecrã. Lido na imprensa.',
    groups: [
      {
        eyebrow: 'Televisão',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18. Usados no ecrã por Mélanie Taravant, Maya Lauque, Marie Drucker, Karine Ferri, Anggun, Caroline Roux, Isabelle Ithurburu, Laurie Cholewa, Hélène Mannarino, Pauline Sanzey e Ava Djamshidi.'
      },
      {
        eyebrow: 'Imprensa',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '"Estas ankle boots Kendrick Paris cumprem todos os requisitos."',
        footer: '- Kahina Sekkai, editora-chefe web, Vanity Fair France'
      },
      {
        text: '"São as mais confortáveis do mundo e, além de serem verdadeiramente bonitas, recebo elogios sempre que as uso."',
        footer: '- Ava Djamshidi, T18 (TV)',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  ja: {
    eyebrow: '記録として',
    title: '画面で見られ、誌面で語られる。',
    groups: [
      {
        eyebrow: 'テレビ',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18。Mélanie Taravant、Maya Lauque、Marie Drucker、Karine Ferri、Anggun、Caroline Roux、Isabelle Ithurburu、Laurie Cholewa、Hélène Mannarino、Pauline Sanzey、Ava Djamshidiが番組内で着用。'
      },
      {
        eyebrow: 'プレス',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '「Kendrick Parisのこのアンクルブーツは、すべての条件を満たしています。」',
        footer: '- Kahina Sekkai、Vanity Fair France ウェブ編集長'
      },
      {
        text: '「地球上で一番快適です。本当に美しい上に、履くたびに褒められます。」',
        footer: '- Ava Djamshidi、T18（TV）',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  ko: {
    eyebrow: '기록',
    title: '화면에서 보이고, 지면에서 읽히다.',
    groups: [
      {
        eyebrow: '텔레비전',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18. Mélanie Taravant, Maya Lauque, Marie Drucker, Karine Ferri, Anggun, Caroline Roux, Isabelle Ithurburu, Laurie Cholewa, Hélène Mannarino, Pauline Sanzey, Ava Djamshidi가 방송에서 착용했습니다.'
      },
      {
        eyebrow: '프레스',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '"이 Kendrick Paris 앵클부츠는 모든 조건을 충족합니다."',
        footer: '- Kahina Sekkai, Vanity Fair France 웹 편집장'
      },
      {
        text: '"세상에서 가장 편안하고, 정말 아름다워서 신을 때마다 칭찬을 받습니다."',
        footer: '- Ava Djamshidi, T18 (TV)',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  de: {
    eyebrow: 'Belegt',
    title: 'Auf dem Bildschirm gesehen. In der Presse gelesen.',
    groups: [
      {
        eyebrow: 'Fernsehen',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18. Auf dem Bildschirm getragen von Mélanie Taravant, Maya Lauque, Marie Drucker, Karine Ferri, Anggun, Caroline Roux, Isabelle Ithurburu, Laurie Cholewa, Hélène Mannarino, Pauline Sanzey und Ava Djamshidi.'
      },
      {
        eyebrow: 'Presse',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '"Diese Ankle Boots von Kendrick Paris erfüllen alle Kriterien."',
        footer: '- Kahina Sekkai, Chefredakteurin Web, Vanity Fair France'
      },
      {
        text: '"Sie sind die bequemsten der Welt und außerdem wirklich schön. Jedes Mal, wenn ich sie trage, bekomme ich nur Komplimente."',
        footer: '- Ava Djamshidi, T18 (TV)',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  it: {
    eyebrow: 'Nei fatti',
    title: 'Visto sullo schermo. Letto sulla stampa.',
    groups: [
      {
        eyebrow: 'Televisione',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18. Indossate in onda da Mélanie Taravant, Maya Lauque, Marie Drucker, Karine Ferri, Anggun, Caroline Roux, Isabelle Ithurburu, Laurie Cholewa, Hélène Mannarino, Pauline Sanzey e Ava Djamshidi.'
      },
      {
        eyebrow: 'Stampa',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '"Questi ankle boots Kendrick Paris hanno tutte le qualità giuste."',
        footer: '- Kahina Sekkai, caporedattrice web, Vanity Fair France'
      },
      {
        text: '"Sono le più comode del mondo e, oltre a essere davvero belle, ricevo complimenti ogni volta che le indosso."',
        footer: '- Ava Djamshidi, T18 (TV)',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  },
  zh: {
    eyebrow: '事实记录',
    title: '出现在荧幕上，也见诸媒体。',
    groups: [
      {
        eyebrow: '电视',
        text: 'France TV · TF1 · Canal+ · beIN Sports · M6 · T18。Mélanie Taravant、Maya Lauque、Marie Drucker、Karine Ferri、Anggun、Caroline Roux、Isabelle Ithurburu、Laurie Cholewa、Hélène Mannarino、Pauline Sanzey、Ava Djamshidi曾在节目中穿着。'
      },
      {
        eyebrow: '媒体',
        text: "Madame Figaro · Vanity Fair · L'Officiel · Grazia · VOICI · Palace Scope · PAP Magazine · BASIC · Purplehaze Magazine."
      }
    ],
    quotes: [
      {
        text: '“这双Kendrick Paris短靴满足所有条件。”',
        footer: '- Kahina Sekkai，Vanity Fair France 网站主编'
      },
      {
        text: '“它们是世界上最舒服的鞋，而且真的很美。我每次穿都会收到称赞。”',
        footer: '- Ava Djamshidi，T18（电视）',
        footerHref: 'https://www.instagram.com/ava.djamshidi/'
      }
    ]
  }
}

const UI_COPY: Record<EditorialStoryLocale, EditorialUiCopy> = {
  fr: { readMore: 'Lire la suite', readLess: 'Lire moins' },
  en: { readMore: 'Read more', readLess: 'Read less' },
  es: { readMore: 'Leer más', readLess: 'Leer menos' },
  pt: { readMore: 'Ler mais', readLess: 'Ler menos' },
  ja: { readMore: '続きを読む', readLess: '閉じる' },
  ko: { readMore: '더 읽기', readLess: '접기' },
  de: { readMore: 'Mehr lesen', readLess: 'Weniger lesen' },
  it: { readMore: 'Leggi di più', readLess: 'Leggi meno' },
  zh: { readMore: '阅读更多', readLess: '收起' }
}

const normalizeEditorialLocale = (locale: EditorialStoryLocale | string): EditorialStoryLocale => (
  locale in STORY_COPY ? locale as EditorialStoryLocale : 'en'
)

export const getEditorialStoryCopy = (locale: EditorialStoryLocale | string) => STORY_COPY[normalizeEditorialLocale(locale)]


export type HomeManifestoCopy = { eyebrow: string, text: string, cta: string }

// Short inspiring line shown on the home page. The long origin story lives in
// STORY_COPY[locale].origin and in doc/home-origin-story.md.
const HOME_MANIFESTO: Record<EditorialStoryLocale, HomeManifestoCopy> = {
  en: { eyebrow: 'The origin', text: 'Some forms are not invented. They are found — in leather, by hand, with patience. A heel that holds. A line that lets a woman stand exactly as she is.', cta: 'Discover the house' },
  fr: { eyebrow: 'L\'origine', text: 'Certaines formes ne s’inventent pas. Elles se trouvent — dans le cuir, à la main, avec patience. Un talon qui tient. Une ligne qui laisse une femme se tenir exactement comme elle est.', cta: 'Découvrir la maison' },
  es: { eyebrow: 'El origen', text: 'Algunas formas no se inventan. Se encuentran — en el cuero, a mano, con paciencia. Un tacón que sostiene. Una línea que deja a una mujer estar exactamente como es.', cta: 'Descubrir la casa' },
  pt: { eyebrow: 'A origem', text: 'Algumas formas não se inventam. Encontram-se — no couro, à mão, com paciência. Um salto que sustenta. Uma linha que deixa uma mulher estar exatamente como é.', cta: 'Descobrir a casa' },
  ja: { eyebrow: '原点', text: '形は、発明されるものではありません。革の中に、手仕事の中に、忍耐の中に見つかるもの。支えるヒール。女性がありのままに立てる一本のライン。', cta: 'メゾンを知る' },
  ko: { eyebrow: '기원', text: '어떤 형태는 만들어지는 것이 아니라 발견됩니다. 가죽 속에서, 손끝에서, 인내 끝에서. 단단히 받쳐 주는 힐. 여성이 있는 그대로 서게 하는 선.', cta: '메종 보기' },
  de: { eyebrow: 'Der Ursprung', text: 'Manche Formen werden nicht erfunden. Man findet sie — im Leder, von Hand, mit Geduld. Ein Absatz, der trägt. Eine Linie, die eine Frau genau so stehen lässt, wie sie ist.', cta: 'Das Haus entdecken' },
  it: { eyebrow: 'L\'origine', text: 'Alcune forme non si inventano. Si trovano — nella pelle, a mano, con pazienza. Un tacco che regge. Una linea che lascia una donna stare esattamente com’è.', cta: 'Scoprire la maison' },
  zh: { eyebrow: '起源', text: '有些形态不是发明出来的，而是被找到的——在皮革里，在手中，在耐心里。一只稳稳承托的鞋跟，一道让女性如其所是地站立的线条。', cta: '探索品牌' },
}

export const getHomeManifestoCopy = (locale: EditorialStoryLocale | string) => HOME_MANIFESTO[normalizeEditorialLocale(locale)]

export const getEditorialRecordCopy = (locale: EditorialStoryLocale | string) => RECORD_COPY[normalizeEditorialLocale(locale)]

export const getEditorialUiCopy = (locale: EditorialStoryLocale | string) => UI_COPY[normalizeEditorialLocale(locale)]
